import React, { useState, useEffect, useRef } from 'react';
import { Form, Button, Spinner, InputGroup, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faShoppingCart, faCheckCircle, faExclamationTriangle,
  faUser, faEnvelope, faPhone, faMapMarkerAlt,
  faGraduationCap, faTag, faReceipt, faDollarSign
} from '@fortawesome/free-solid-svg-icons';
import API_CONFIG2 from '../../API_CONFIG/API_CONFIG2';
import { motion, AnimatePresence } from 'framer-motion';

// ========================
// Cache global des formations
// ========================
const formationsCache = {
  data: null,
  isLoading: false,
  promise: null,
  error: null
};

const loadFormations = async (apiUrl) => {
  if (formationsCache.data) {
    return formationsCache.data;
  }
  if (formationsCache.isLoading) {
    return formationsCache.promise;
  }
  formationsCache.isLoading = true;
  formationsCache.error = null;

  formationsCache.promise = fetch(apiUrl)
    .then(res => {
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    })
    .then(data => {
      if (data.status === 'success') {
        formationsCache.data = data.data;
        return data.data;
      } else {
        throw new Error(data.message || 'Erreur chargement formations');
      }
    })
    .catch(err => {
      formationsCache.error = err;
      throw err;
    })
    .finally(() => {
      formationsCache.isLoading = false;
      formationsCache.promise = null;
    });

  return formationsCache.promise;
};

// ========================
// Fetch avec retry (gestion 429)
// ========================
const fetchWithRetry = async (url, options = {}, retries = 3, baseDelay = 1000) => {
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const response = await fetch(url, options);
      const text = await response.text();
      console.log(`📡 Réponse (tentative ${attempt + 1}) :`, text.substring(0, 200));

      if (response.status === 429 && attempt < retries) {
        const retryAfter = response.headers.get('Retry-After');
        const delay = retryAfter
          ? parseInt(retryAfter, 10) * 1000
          : baseDelay * Math.pow(2, attempt);
        console.warn(`⏳ 429 détecté, nouvelle tentative dans ${delay}ms`);
        await new Promise(resolve => setTimeout(resolve, delay));
        continue;
      }

      if (!response.ok) {
        throw new Error(`HTTP ${response.status} : ${text || 'Erreur inconnue'}`);
      }

      try {
        return JSON.parse(text);
      } catch (parseError) {
        throw new Error(`Réponse JSON invalide : ${text.substring(0, 200) || '(réponse vide)'}`);
      }
    } catch (error) {
      if (attempt === retries) throw error;
      const delay = baseDelay * Math.pow(2, attempt);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
};

// ========================
// Hook personnalisé pour les formations
// ========================
const useFormations = () => {
  const [formations, setFormations] = useState(formationsCache.data || []);
  const [loading, setLoading] = useState(!formationsCache.data);
  const [error, setError] = useState(null);
  const abortControllerRef = useRef(null);

  useEffect(() => {
    if (formationsCache.data) {
      setFormations(formationsCache.data);
      setLoading(false);
      return;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;
    let isMounted = true;

    const fetchData = async () => {
      setLoading(true);
      try {
        const uniqueParam = new Date().getTime(); // Empêche le cache
        const data = await loadFormations(`${API_CONFIG2.apiUrl}formation/api_formation2.php?&unique=${uniqueParam}`);
        if (isMounted) {
          setFormations(data);
          setError(null);
        }
      } catch (err) {
        if (isMounted && err.name !== 'AbortError') {
          setError(err.message || 'Impossible de charger les formations');
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchData();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, []);

  return { formations, loading, error };
};

// ========================
// Composant principal – Thème Dark Blue Glassmorphism
// ========================
const AchatFormation = ({ userId }) => {
  const { formations, loading, error: formationsError } = useFormations();
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', variant: 'success' });
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [purchaseDetails, setPurchaseDetails] = useState(null);

  const [formData, setFormData] = useState({
    formation_id: '',
    nom: '',
    email: '',
    telephone: '',
    adresse: ''
  });

  useEffect(() => {
    if (formationsError) {
      setToast({ show: true, message: formationsError, variant: 'danger' });
      const timer = setTimeout(() => setToast(prev => ({ ...prev, show: false })), 5000);
      return () => clearTimeout(timer);
    }
  }, [formationsError]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    setSubmitting(true);
    try {
      const payload = { ...formData, userId };
      const data = await fetchWithRetry(`${API_CONFIG2.apiUrl}achat/api_achat.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (data.status === 'success') {
        setToast({ show: true, message: data.message, variant: 'success' });
        setTimeout(() => setToast(prev => ({ ...prev, show: false })), 5000);
        setPurchaseDetails({
          ...data,
          formation: formations.find(f => f.id === parseInt(formData.formation_id)),
          clientNom: formData.nom,
          clientEmail: formData.email
        });
        setShowSuccessModal(true);
        setFormData({ formation_id: '', nom: '', email: '', telephone: '', adresse: '' });
      } else {
        throw new Error(data.message || 'Erreur inconnue');
      }
    } catch (error) {
      setToast({ show: true, message: error.message, variant: 'danger' });
      setTimeout(() => setToast(prev => ({ ...prev, show: false })), 5000);
    } finally {
      setSubmitting(false);
    }
  };

  const selectedFormation = formations.find(f => f.id === parseInt(formData.formation_id));
  const prix = selectedFormation ? parseFloat(selectedFormation.prix) : 0;
  const tva = prix * 16 / 116;
  const total = prix;

  return (
    <div className="achat-formation-wrapper">
      <style>{`
        :root {
          --bg-primary: #0a0e27;         /* Fond bleu nuit profond */
          --card-bg: rgba(13, 17, 38, 0.65); /* Carte semi-transparente */
          --card-border: rgba(79, 156, 245, 0.18);
          --accent-blue: #4f9cf5;        /* Bleu électrique */
          --accent-glow: rgba(79, 156, 245, 0.5);
          --text-primary: #e0e7ff;
          --text-secondary: #8892b0;
        }

        .achat-formation-wrapper {
          padding: 30px;
          background: var(--bg-primary);
          color: var(--text-primary);
          font-family: 'Inter', sans-serif;
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          overflow: hidden;
        }

        /* Effet de fond animé subtil (optionnel) */
        .achat-formation-wrapper::before {
          content: '';
          position: absolute;
          width: 600px;
          height: 600px;
          background: radial-gradient(circle, rgba(79,156,245,0.15) 0%, transparent 70%);
          top: -200px;
          right: -100px;
          border-radius: 50%;
          z-index: 0;
        }

        .glass-card {
          position: relative;
          z-index: 1;
          background: var(--card-bg);
          backdrop-filter: blur(30px);
          -webkit-backdrop-filter: blur(30px);
          border: 1px solid var(--card-border);
          border-radius: 32px;
          padding: 2.5rem;
          box-shadow: 0 20px 60px rgba(0, 20, 50, 0.8), inset 0 0 20px rgba(79,156,245,0.08);
          max-width: 720px;
          width: 100%;
          margin: 0 auto;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        @media (prefers-reduced-motion: no-preference) {
          .glass-card:hover {
            transform: translateY(-5px);
            box-shadow: 0 30px 80px rgba(0, 40, 100, 0.9), 0 0 30px var(--accent-glow);
          }
        }

        .section-title {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 2rem;
          border-bottom: 1px solid rgba(79, 156, 245, 0.1);
          padding-bottom: 1rem;
        }
        .section-title h2 {
          font-weight: 700;
          background: linear-gradient(135deg, #a5c9ff, #4f9cf5);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          margin: 0;
          font-size: 1.8rem;
        }

        .monetra-input, .monetra-select {
          background: rgba(16, 23, 55, 0.5);
          border: 1px solid rgba(79, 156, 245, 0.15);
          border-radius: 14px;
          color: #e0e7ff;
          padding: 12px 16px;
          width: 100%;
          transition: all 0.3s ease;
          font-size: 1rem;
        }
        .monetra-input:focus, .monetra-select:focus {
          border-color: var(--accent-blue);
          outline: none;
          background: rgba(25, 35, 80, 0.7);
          box-shadow: 0 0 0 4px rgba(79, 156, 245, 0.15);
        }
        .monetra-input::placeholder, .monetra-select::placeholder {
          color: rgba(160, 180, 220, 0.4);
        }

        .input-group-glass {
          background: transparent;
          border: 1px solid rgba(79, 156, 245, 0.15);
          border-radius: 14px;
          transition: all 0.3s ease;
        }
        .input-group-glass .input-group-text {
          background: rgba(16, 23, 55, 0.6);
          border: none;
          color: var(--text-secondary);
          border-radius: 14px 0 0 14px;
        }
        .input-group-glass .monetra-input {
          border: none;
          border-radius: 0 14px 14px 0;
        }
        .input-group-glass:focus-within {
          border-color: var(--accent-blue);
          box-shadow: 0 0 0 4px rgba(79, 156, 245, 0.15);
        }

        .monetra-select option {
          background: #0b1124;
          color: #e0e7ff;
        }

        .monetra-btn {
          padding: 14px 28px;
          border-radius: 30px;
          background: linear-gradient(135deg, #4f9cf5, #3a7bd5);
          color: #fff;
          border: none;
          font-weight: 600;
          font-size: 1.05rem;
          transition: all 0.3s cubic-bezier(0.2, 0.8, 0.2, 1);
          box-shadow: 0 6px 20px rgba(79, 156, 245, 0.4);
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }
        .monetra-btn:hover:not(:disabled) {
          transform: translateY(-3px);
          box-shadow: 0 10px 30px rgba(79, 156, 245, 0.6);
          background: linear-gradient(135deg, #5da8ff, #4a8de5);
        }
        .monetra-btn:disabled {
          background: #1f2a48;
          color: #556;
          box-shadow: none;
        }

        .price-summary {
          background: rgba(10, 18, 40, 0.55);
          border-radius: 20px;
          padding: 1.5rem;
          margin: 1.5rem 0;
          border: 1px solid rgba(79, 156, 245, 0.12);
          backdrop-filter: blur(12px);
        }
        .price-summary .row {
          display: flex;
          justify-content: space-between;
          padding: 6px 0;
          font-size: 0.95rem;
        }
        .price-summary .total {
          font-weight: 700;
          font-size: 1.4rem;
          color: var(--accent-blue);
          border-top: 1px solid rgba(79, 156, 245, 0.15);
          padding-top: 12px;
          margin-top: 8px;
        }

        .toast-custom {
          background: rgba(10, 14, 39, 0.9) !important;
          backdrop-filter: blur(20px);
          border: 1px solid rgba(79, 156, 245, 0.3) !important;
          border-radius: 16px !important;
          color: #e0e7ff !important;
          box-shadow: 0 20px 50px rgba(0, 0, 50, 0.7) !important;
        }

        .monetra-modal .modal-content {
          background: rgba(10, 14, 39, 0.95);
          backdrop-filter: blur(30px);
          border: 1px solid rgba(79, 156, 245, 0.2);
          border-radius: 32px;
          color: #e0e7ff;
          box-shadow: 0 30px 80px rgba(0, 10, 50, 0.9);
        }
        .monetra-modal .modal-header {
          border-bottom: 1px solid rgba(79, 156, 245, 0.1);
        }
        .monetra-modal .modal-footer {
          border-top: 1px solid rgba(79, 156, 245, 0.1);
        }
        .monetra-modal .btn-close {
          filter: invert(0.8) sepia(1) saturate(5) hue-rotate(180deg);
          opacity: 0.6;
        }

        .success-icon {
          color: var(--accent-blue);
          font-size: 4rem;
          animation: pulse 1.5s ease-in-out infinite;
          filter: drop-shadow(0 0 10px rgba(79, 156, 245, 0.6));
        }
        @keyframes pulse {
          0%, 100% { transform: scale(1); opacity: 0.8; }
          50% { transform: scale(1.1); opacity: 1; }
        }

        /* ============ RESPONSIVE MOBILE ============ */
        @media (max-width: 768px) {
          .achat-formation-wrapper {
            padding: 15px;
          }
          .glass-card {
            padding: 1.2rem;
            border-radius: 24px;
          }
          .section-title {
            margin-bottom: 1.2rem;
          }
          .section-title h2 {
            font-size: 1.4rem;
          }
          .monetra-input, .monetra-select {
            font-size: 14px;
            padding: 10px 14px;
          }
          .monetra-btn {
            font-size: 1rem;
            padding: 12px 20px;
          }
          .price-summary {
            padding: 1rem;
          }
          .price-summary .row {
            font-size: 0.85rem;
          }
          .price-summary .total {
            font-size: 1.1rem;
          }
          .achat-formation-wrapper .row > [class*="col-"] {
            width: 100%;
            max-width: 100%;
            flex: 0 0 100%;
          }
          .success-icon {
            font-size: 3rem;
          }
          .monetra-modal .modal-content {
            border-radius: 20px;
          }
        }
        @media (max-width: 576px) {
          .section-title h2 {
            font-size: 1.2rem;
          }
          .price-summary .row {
            font-size: 0.8rem;
          }
          .monetra-btn {
            font-size: 0.9rem;
          }
        }
      `}</style>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        <div className="glass-card">
          <div className="section-title">
            <FontAwesomeIcon icon={faShoppingCart} style={{ color: 'var(--accent-blue)', fontSize: '1.8rem' }} />
            <h2>Vente formation</h2>
          </div>

          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label className="text-secondary small">
                <FontAwesomeIcon icon={faGraduationCap} className="me-2" />
                Formation
              </Form.Label>
              <Form.Select
                name="formation_id"
                value={formData.formation_id}
                onChange={handleChange}
                required
                className="monetra-select"
                disabled={loading}
              >
                <option value="">-- Choisir une formation --</option>
                {formations.map(f => (
                  <option key={f.id} value={f.id}>
                    {f.titre} - {parseFloat(f.prix).toFixed(2)}$
                  </option>
                ))}
              </Form.Select>
              {loading && <div className="mt-2 text-secondary small"><Spinner animation="border" size="sm" className="me-2" /> Chargement...</div>}
            </Form.Group>

            <div className="row g-3">
              <div className="col-md-6 col-12">
                <Form.Group>
                  <Form.Label className="text-secondary small">
                    <FontAwesomeIcon icon={faUser} className="me-2" />
                    Nom complet
                  </Form.Label>
                  <InputGroup className="input-group-glass">
                    <InputGroup.Text><FontAwesomeIcon icon={faUser} /></InputGroup.Text>
                    <Form.Control
                      type="text"
                      name="nom"
                      value={formData.nom}
                      onChange={handleChange}
                      required
                      className="monetra-input text-primary"
                      placeholder="Votre nom"
                    />
                  </InputGroup>
                </Form.Group>
              </div>
              <div className="col-md-6 col-12">
                <Form.Group>
                  <Form.Label className="text-secondary small">
                    <FontAwesomeIcon icon={faEnvelope} className="me-2" />
                    Email
                  </Form.Label>
                  <InputGroup className="input-group-glass">
                    <InputGroup.Text><FontAwesomeIcon icon={faEnvelope} /></InputGroup.Text>
                    <Form.Control
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="monetra-input  text-primary"
                      placeholder="exemple@email.com"
                    />
                  </InputGroup>
                </Form.Group>
              </div>
              <div className="col-md-6 col-12">
                <Form.Group>
                  <Form.Label className="text-secondary small">
                    <FontAwesomeIcon icon={faPhone} className="me-2" />
                    Téléphone
                  </Form.Label>
                  <InputGroup className="input-group-glass">
                    <InputGroup.Text><FontAwesomeIcon icon={faPhone} /></InputGroup.Text>
                    <Form.Control
                      type="text"
                      name="telephone"
                      value={formData.telephone}
                      onChange={handleChange}
                      required
                      className="monetra-input  text-primary"
                      placeholder="+33 6 00 00 00 00"
                    />
                  </InputGroup>
                </Form.Group>
              </div>
              <div className="col-md-6 col-12">
                <Form.Group>
                  <Form.Label className="text-secondary small">
                    <FontAwesomeIcon icon={faMapMarkerAlt} className="me-2" />
                    Adresse
                  </Form.Label>
                  <InputGroup className="input-group-glass">
                    <InputGroup.Text><FontAwesomeIcon icon={faMapMarkerAlt} /></InputGroup.Text>
                    <Form.Control
                      as="textarea"
                      name="adresse"
                      value={formData.adresse}
                      onChange={handleChange}
                      required
                      className="monetra-input text-primary"
                      placeholder="Votre adresse complète"
                      rows={2}
                    />
                  </InputGroup>
                </Form.Group>
              </div>
            </div>

            <AnimatePresence>
              {selectedFormation && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="price-summary">
                    <div className="row">
                      <span><FontAwesomeIcon icon={faTag} className="me-2" style={{ color: 'var(--text-secondary)' }} />Prix de la formation</span>
                      <span>{prix.toFixed(2)}$</span>
                    </div>
                    <div className="row">
                      <span><FontAwesomeIcon icon={faReceipt} className="me-2" style={{ color: 'var(--text-secondary)' }} />TVA (16%)</span>
                      <span>{tva.toFixed(2)}$</span>
                    </div>
                    <div className="row total">
                      <span><FontAwesomeIcon icon={faDollarSign} className="me-2" />Total TTC</span>
                      <span>{total.toFixed(2)}$</span>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <Button
              type="submit"
              className="monetra-btn w-100 mt-3"
              disabled={submitting || !formData.formation_id}
            >
              {submitting ? (
                <><Spinner as="span" animation="border" size="sm" className="me-2" /> Traitement...</>
              ) : (
                'Valider l\'achat'
              )}
            </Button>
          </Form>
        </div>
      </motion.div>

      {/* Modal de confirmation */}
      <Modal show={showSuccessModal} onHide={() => setShowSuccessModal(false)} centered className="monetra-modal" size="lg">
        <Modal.Header closeButton>
          <Modal.Title className="fw-bold">
            <FontAwesomeIcon icon={faCheckCircle} className="me-2" style={{ color: 'var(--accent-blue)' }} />
            Achat confirmé
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-4">
          <div className="mb-4">
            <FontAwesomeIcon icon={faCheckCircle} className="success-icon" />
          </div>
          <h4 className="fw-bold mb-2">Félicitations !</h4>
          <p className="text-secondary">Votre achat a été enregistré avec succès.</p>
          {purchaseDetails && (
            <div className="mt-4 bg-dark bg-opacity-25 p-3 rounded-3 text-start">
              <p className="mb-1"><strong>Formation :</strong> {purchaseDetails.formation?.titre}</p>
              <p className="mb-1"><strong>Client :</strong> {purchaseDetails.clientNom}</p>
              <p className="mb-1"><strong>Email :</strong> {purchaseDetails.clientEmail}</p>
              <p className="mb-0"><strong>Numéro de facture :</strong> #{purchaseDetails.facture_id}</p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowSuccessModal(false)}>Fermer</Button>
        </Modal.Footer>
      </Modal>

      {/* Toast */}
      <div style={{ position: 'fixed', top: '20px', right: '20px', zIndex: 9999 }}>
        <div className={`toast-custom p-3 ${toast.show ? 'd-block' : 'd-none'}`} style={{ minWidth: '300px' }}>
          <div className="d-flex align-items-center gap-3">
            <FontAwesomeIcon icon={toast.variant === 'success' ? faCheckCircle : faExclamationTriangle} className={toast.variant === 'success' ? 'text-success' : 'text-danger'} />
            <span>{toast.message}</span>
            <button className="btn-close-custom ms-auto" onClick={() => setToast({ ...toast, show: false })} style={{ background: 'transparent', border: 'none', color: '#fff', opacity: 0.5 }}>✕</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AchatFormation;